import type { MouseEvent, ReactNode } from "react";

import dayjs from "dayjs";
import { styled } from "styled-components";

import { shortMonthDayAndYear, TextCell, type ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes, fonts } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { SwTrack } from "app/services/SwTrack";
import { allTrackers } from "app/services/track/track";
import { CoreSegmentCell } from "pro-features/components/core cells/src/CoreSegmentCell/CoreSegmentCell";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ROWS_PER_PAGE } from "../constants/common";
import { itsMyOwnSegment } from "../itsMyOwnSegment";
import { MODE } from "../SegmentsAnalysisTrafficContainerMode";
import { getSegmentWizardRoute } from "../utils/routing";

import type { WebsiteSegmentRow } from "./WebsiteSegmentsTableTypes";

type SortOrder = "ascend" | "descend";
type SegmentRow = WebsiteSegmentRow;
type TableColumns = ColumnsType<SegmentRow>;
type RowActionRenderer = (props: { row: SegmentRow }) => ReactNode;
type Translate = (key: string, values?: Record<string, unknown>) => string;

interface IGetWebsiteSegmentsColumnsParams {
    currentPage: number;
    totalCount: number;
    sortedField: string;
    sortOrder: SortOrder;
    currentModule: string;
    rowActionRenderers: RowActionRenderer[];
    translate: Translate;
    showOwnerColumn?: boolean;
}

const CellWrapper = styled.div`
    height: 100%;
`;

const DateModifiedWrapper = styled.div`
    cursor: pointer;
`;

const LastColumnCellContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const LastColumnValueContainer = styled.div`
    min-width: 0;
    flex: 1;
`;

const OwnerLabel = styled.span`
    letter-spacing: 0.6px;
    font-family: ${fonts.$robotoFontFamily};
    font-size: 8px;
    border-radius: 10px;
    margin: 3px 0 0 9px;
    font-weight: bold;
    color: ${colorsPalettes.carbon[0]};
    text-transform: uppercase;
    padding: 4px 5px 3px 5px;
    height: 15px;
    line-height: 1;
    background-color: ${colorsPalettes.midnight[200]};
`;

const onAnalyzeSegment = (row: SegmentRow, currentModule: string) => {
    if (!row) {
        return;
    }

    const isBlacklisted = !!row.isBlacklisted;
    const isDisabled = row.segmentAdvancedDisabled || isBlacklisted;
    if (isDisabled) {
        return;
    }

    /* eslint-disable @typescript-eslint/naming-convention -- Tracking API requires snake_case field names */
    allTrackers.dispatchTrackEvent("Internal link", "click", "SegmentAnalysis/Analyze segment", {
        Segment_Domain: row.domain,
        Segment_Name: row.segmentName,
        Segment_ID: row.id,
        Segment_Type: "custom",
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    const segmentAnalysisStateName = `${currentModule}-analysis-traffic`;

    void proNavigator.go(segmentAnalysisStateName, {
        id: row.id,
        country: 840,
        mode: MODE.single,
        duration: "6m",
        ignoreFilterConsistency: true,
        ...SegmentsUtils.getPageFilterParams(row.id),
    });
};

const getIndex = (index: number, page: number, pageSize: number) => {
    return page > 1 ? page * pageSize - pageSize + (index + 1) : index + 1;
};

export const getWebsiteSegmentsColumns = ({
    currentPage,
    totalCount,
    sortedField,
    sortOrder,
    currentModule,
    rowActionRenderers,
    translate,
    showOwnerColumn = true,
}: IGetWebsiteSegmentsColumnsParams): TableColumns => {
    const BlacklistedTooltip = ({ children }: { children: ReactNode }) => (
        <Tooltip
            content={translate("segment.analysis.org.table.column.disabled.tooltip")}
            placement="top"
        >
            <div className="segments-table-row-disabled-active">{children}</div>
        </Tooltip>
    );
    const analyzeButton = rowActionRenderers[0];
    const actionButton = rowActionRenderers[1];
    const getInlineActions = (row: SegmentRow) => {
        if (!analyzeButton && !actionButton) {
            return null;
        }

        return (
            <div className="segments-table-hover-actions-inline">
                {analyzeButton?.({ row })}
                {actionButton?.({ row })}
            </div>
        );
    };

    const columns: TableColumns = [
        {
            key: "index",
            dataIndex: "id",
            title: "",
            width: 48,
            align: "center",
            hiddenInColumnPicker: true,
            render: (_, row, index) => {
                const isDisabled = !!row.isBlacklisted && itsMyOwnSegment(row.userId);
                return (
                    <div className={`u-alignCenter ${isDisabled ? "segments-table-row-disabled" : ""}`}>
                        {getIndex(index, currentPage, ROWS_PER_PAGE)}
                    </div>
                );
            },
        },
        {
            key: "domain",
            dataIndex: "domain",
            title: `${translate("segment.analysis.table.column.segments")} (${totalCount})`,
            sorter: true,
            sortOrder: sortedField === "domain" ? sortOrder : undefined,
            tooltipTitle: translate("segment.analysis.table.column.segments.tooltip"),
            render: (value: SegmentRow["domain"], row) => {
                const subtitleFilters = [
                    {
                        filter: "text",
                        value: row.segmentName,
                    },
                ];
                const isBlacklisted = !!row.isBlacklisted;
                const editSegmentClick = (e: MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    SwTrack.all.trackEvent("Internal Link", "click", `segmentCard/editSegment/${row.id}`);
                    const segmentWizardStateName = getSegmentWizardRoute(currentModule);
                    void proNavigator.go(segmentWizardStateName, { sid: row.id });
                };

                const cell = (
                    <CellWrapper
                        className="py-0"
                        onClick={() => onAnalyzeSegment(row, currentModule)}
                    >
                        <CoreSegmentCell
                            withWebsiteSign={true}
                            segmentName={row.segmentName}
                            lastModified={row.lastUpdated}
                            isOrgSegment={!itsMyOwnSegment(row.userId)}
                            onClick={editSegmentClick}
                            segmentId={row.id}
                            icon={row.favicon}
                            domain={value}
                            subtitleFilters={subtitleFilters}
                            className={isBlacklisted ? "segments-table-row-disabled" : ""}
                        />
                    </CellWrapper>
                );

                return isBlacklisted ? <BlacklistedTooltip>{cell}</BlacklistedTooltip> : cell;
            },
        },
        {
            key: "lastUpdated",
            dataIndex: "lastUpdated",
            title: translate("segment.analysis.table.column.creationdate"),
            sorter: true,
            sortOrder: sortedField === "lastUpdated" ? sortOrder : undefined,
            tooltipTitle: translate("segment.analysis.table.column.creationdate.tooltip"),
            render: (value: SegmentRow["lastUpdated"], row) => {
                const isBlacklisted = !!row.isBlacklisted;
                const formattedDate =
                    typeof value === "string" || typeof value === "number"
                        ? dayjs(value).format(shortMonthDayAndYear)
                        : "-";
                const content = (
                    <LastColumnCellContainer>
                        <LastColumnValueContainer>
                            <DateModifiedWrapper onClick={() => onAnalyzeSegment(row, currentModule)}>
                                <TextCell className={isBlacklisted ? "segments-table-row-disabled" : ""}>
                                    {formattedDate}
                                </TextCell>
                            </DateModifiedWrapper>
                        </LastColumnValueContainer>
                        {!showOwnerColumn ? getInlineActions(row) : null}
                    </LastColumnCellContainer>
                );

                return isBlacklisted ? <BlacklistedTooltip>{content}</BlacklistedTooltip> : content;
            },
        },
    ];

    if (showOwnerColumn) {
        columns.push({
            key: "userName",
            dataIndex: "userName",
            title: translate("segment.analysis.org.table.column.username"),
            sorter: true,
            sortOrder: sortedField === "userName" ? sortOrder : undefined,
            tooltipTitle: translate("segment.analysis.org.table.column.username.tooltip"),
            render: (_value: SegmentRow["userName"], row) => {
                const isBlacklisted = !!row.isBlacklisted;
                const ownerName = row.userName?.trim() || "-";
                const content = (
                    <LastColumnCellContainer>
                        <LastColumnValueContainer>
                            <DateModifiedWrapper onClick={() => onAnalyzeSegment(row, currentModule)}>
                                <TextCell className={isBlacklisted ? "segments-table-row-disabled" : ""}>
                                    {ownerName}
                                    {itsMyOwnSegment(row.userId) ? (
                                        <OwnerLabel>
                                            {translate("segment.analysis.org.table.column.username.my.segment")}
                                        </OwnerLabel>
                                    ) : null}
                                </TextCell>
                            </DateModifiedWrapper>
                        </LastColumnValueContainer>
                        {getInlineActions(row)}
                    </LastColumnCellContainer>
                );

                return isBlacklisted ? <BlacklistedTooltip>{content}</BlacklistedTooltip> : content;
            },
        });
    }

    return columns;
};
